import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { Card, Row, Col, Button, Form, Badge } from 'react-bootstrap';
import { getProjectByIdAction } from '../../store/reducers/project';
import { updateAssignmentStatusAction } from '../../store/reducers/personalTask';
import { accountAuthInfoPagingAction } from '../../store/reducers/account';
import { TASK_STATUS_CONST, TASK_PRIORITY_CONST } from '../../store/constants';
import { toast } from 'react-toastify';
import { getUser } from '../../utils/oidc';

const getStatusStyle = (color) => {
    const map = {
        'secondary': { bg: '#f6f8fa', text: '#57606a', border: '#d0d7de' },
        'info': { bg: '#ddf4ff', text: '#0969da', border: '#54aeff' },
        'warning': { bg: '#fff8c5', text: '#9a6700', border: '#d4a72c' },
        'success': { bg: '#dafbe1', text: '#1a7f37', border: '#2da44e' },
        'danger': { bg: '#ffebe9', text: '#cf222e', border: '#ff8182' }
    };
    return map[color] || map['secondary'];
};

// Helper functions matching the base sample
const toInputDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getTodayInputDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const toDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const dt = new Date(dateStr);
    if (Number.isNaN(dt.getTime())) return '';
    const d = String(dt.getDate()).padStart(2, '0');
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const y = dt.getFullYear();
    return `${d}/${m}/${y}`;
};

const COLUMN_STYLE_MAP = {
    0: { // Cần làm
        headerBg: '#e1e5f2',
        headerText: '#333333',
        border: '#c4ccdd',
        bodyBg: '#f7f9fc',
    },
    1: { // Đang làm
        headerBg: '#d3e5ff',
        headerText: '#245b8f',
        border: '#aac6ff',
        bodyBg: '#f3f7ff',
    },
    3: { // Hoàn thành
        headerBg: '#cef4e4',
        headerText: '#247a55',
        border: '#a0e0c6',
        bodyBg: '#f1fbf6',
    },
    4: { // Không hoàn thành
        headerBg: '#f8d7da',
        headerText: '#721c24',
        border: '#f5c6cb',
        bodyBg: '#fff5f5',
    },
};

// Use central constants for status columns
const STATUS_COLUMNS = TASK_STATUS_CONST.map(s => ({
    id: s.value,
    title: s.label,
    color: s.color
}));

const ProjectKanban = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const history = useHistory();
    const { item } = useSelector((state) => state.project);
    const tasks = useMemo(() => {
        return item?.members || [];
    }, [item]);
    const { listAccount } = useSelector((state) => state.account);
    const [currentUserId, setCurrentUserId] = useState(null);

    useEffect(() => {
        getUser().then(u => setCurrentUserId(u?.profile?.sub));
    }, []);
    
    const [formMode, setFormMode] = useState(null); // 'create' or 'edit'
    const [formTask, setFormTask] = useState(null);
    const [formSubmitting, setFormSubmitting] = useState(false);
    
    const [taskForm, setTaskForm] = useState({
        title: '',
        description: '',
        status: 0,
        priority: 2,
        progress: 0,
        startDate: '',
        deadline: '',
        assigneeId: ''
    });

    useEffect(() => {
        dispatch(getProjectByIdAction(id));
    }, [dispatch, id]);

    useEffect(() => {
        if (!listAccount || listAccount.length === 0) {
            dispatch(accountAuthInfoPagingAction({ pageIndex: 1, pageSize: 1000 }));
        }
    }, [dispatch, listAccount]);

    const getAssigneeName = (userId) => {
        if (!userId) return 'Chưa giao';
        // Tìm theo accountId (ID thực tế của Account)
        const accInfo = listAccount?.find(a => String(a.accountId) === String(userId));
        if (accInfo) {
            const fullName = `${accInfo.lastName || ''} ${accInfo.middleName || ''} ${accInfo.firstName || ''}`.replace(/\s+/g, ' ').trim();
            return fullName || accInfo.account?.username || 'Chưa giao';
        }
        return 'Chưa giao';
    };

    const totalProgress = useMemo(() => {
        if (!tasks || tasks.length === 0) return 0;
        const sum = tasks.reduce((acc, t) => {
            const s = t.status ?? t.Status ?? 0;
            return acc + (Number(s) === 3 ? 100 : 0);
        }, 0);
        return Math.round(sum / tasks.length);
    }, [tasks]);

    const handleOpenCreate = () => {
        setFormMode('create');
        setFormTask(null);
        setTaskForm({
            title: '',
            description: '',
            status: 0,
            priority: 2,
            progress: 0,
            startDate: getTodayInputDate(),
            deadline: '',
            assigneeId: ''
        });
    };

    const handleOpenEdit = (task) => {
        setFormMode('edit');
        setFormTask(task);
        setTaskForm({
            title: task.title,
            description: task.description || '',
            status: task.status,
            priority: task.priority,
            progress: task.progress || 0,
            startDate: toInputDate(task.startDate),
            deadline: toInputDate(task.deadline),
            assigneeId: task.assigneeId || ''
        });
    };

    const handleCloseForm = () => {
        setFormMode(null);
        setFormTask(null);
    };

    const handleSaveTask = async (e) => {
        if (e) e.preventDefault();
        if (!taskForm.title.trim()) {
            toast.error('Vui lòng nhập tên công việc');
            return;
        }

        const model = {
            ...taskForm,
            projectId: parseInt(id),
            assigneeId: taskForm.assigneeId || null,
            startDate: taskForm.startDate || null,
            deadline: taskForm.deadline || null,
            progress: taskForm.status === 3 ? 100 : taskForm.progress
        };

        try {
            setFormSubmitting(true);
            if (formMode === 'edit' && formTask) {
                await dispatch(updateTaskAction({ id: formTask.id, data: model })).unwrap();
                toast.success('Cập nhật công việc thành công');
            } else {
                await dispatch(addTaskAction(model)).unwrap();
                toast.success('Thêm công việc thành công');
            }
            handleCloseForm();
            dispatch(getProjectByIdAction(id));
        } catch (err) {
            toast.error('Lưu công việc thất bại');
        } finally {
            setFormSubmitting(false);
        }
    };

    const handleMoveTask = async (newStatus) => {
        try {
            await dispatch(updateAssignmentStatusAction({ 
                projectId: id, 
                data: { status: newStatus } 
            })).unwrap();
            dispatch(getProjectByIdAction(id));
            toast.success('Cập nhật trạng thái thành công');
        } catch (err) {
            toast.error('Chuyển trạng thái thất bại');
        }
    };

    const handleDeleteTask = async (taskId) => {
        if (window.confirm('Bạn có chắc muốn xoá công việc này?')) {
            try {
                await dispatch(deleteTaskAction(taskId)).unwrap();
                dispatch(getProjectByIdAction(id));
                toast.success('Xoá công việc thành công');
            } catch (err) {
                toast.error('Xoá công việc thất bại');
            }
        }
    };

    const renderTaskForm = () => (
        <Card className="mb-3 shadow-sm border-primary">
            <Card.Body className="p-2">
                <h5 className="mb-3" style={{ fontSize: 15 }}>
                    {formMode === 'edit' ? 'Cập nhật công việc' : 'Thêm công việc mới'}
                </h5>
                <Form onSubmit={handleSaveTask}>
                    <Form.Group className="mb-2">
                        <Form.Control 
                            type="text" 
                            placeholder="Tên công việc..." 
                            value={taskForm.title} 
                            onChange={(e) => setTaskForm({...taskForm, title: e.target.value})} 
                            size="sm" 
                        />
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Control 
                            as="textarea" 
                            rows={3} 
                            placeholder="Mô tả..." 
                            value={taskForm.description} 
                            onChange={(e) => setTaskForm({...taskForm, description: e.target.value})} 
                            size="sm" 
                        />
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Control 
                            as="select" 
                            value={taskForm.assigneeId} 
                            onChange={(e) => setTaskForm({...taskForm, assigneeId: e.target.value})} 
                            size="sm"
                        >
                            <option value="">-- Người phụ trách (tuỳ chọn) --</option>
                            {item?.members?.map(m => (
                                <option key={m.userId} value={m.userId}>{getAssigneeName(m.userId)}</option>
                            ))}
                        </Form.Control>
                    </Form.Group>
                    <Form.Group className="mb-2">
                        <Form.Label className="mb-1" style={{ fontSize: 12, fontWeight: 600 }}>Ngày bắt đầu</Form.Label>
                        <Form.Control 
                            type="date" 
                            value={taskForm.startDate} 
                            onChange={(e) => setTaskForm({...taskForm, startDate: e.target.value})} 
                            size="sm" 
                        />
                    </Form.Group>
                    <Row>
                        <Col md={12} className="mb-2">
                            <Form.Label className="mb-1" style={{ fontSize: 12, fontWeight: 600 }}>Mức độ ưu tiên</Form.Label>
                            <Form.Control 
                                as="select" 
                                value={taskForm.priority} 
                                onChange={(e) => setTaskForm({...taskForm, priority: parseInt(e.target.value)})} 
                                size="sm"
                            >
                                {TASK_PRIORITY_CONST.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </Form.Control>
                        </Col>
                        <Col md={12} className="mb-2">
                            <Form.Label className="mb-1" style={{ fontSize: 12, fontWeight: 600 }}>Tiến độ (%)</Form.Label>
                            <Form.Control 
                                type="number" 
                                min="0" 
                                max="100" 
                                value={taskForm.progress} 
                                onChange={(e) => setTaskForm({...taskForm, progress: parseInt(e.target.value) || 0})} 
                                size="sm" 
                            />
                        </Col>
                        <Col md={12} className="mb-2">
                            <Form.Label className="mb-1" style={{ fontSize: 12, fontWeight: 600 }}>Hạn chót</Form.Label>
                            <Form.Control 
                                type="date" 
                                value={taskForm.deadline} 
                                onChange={(e) => setTaskForm({...taskForm, deadline: e.target.value})} 
                                size="sm" 
                            />
                        </Col>
                    </Row>
                    <div className="d-flex justify-content-between mt-3">
                        <Button variant="primary" size="sm" type="submit" disabled={formSubmitting}>
                            {formMode === 'edit' ? 'Lưu' : 'Thêm'}
                        </Button>
                        <Button variant="secondary" size="sm" onClick={handleCloseForm} disabled={formSubmitting}>
                            Hủy
                        </Button>
                    </div>
                </Form>
            </Card.Body>
        </Card>
    );

    return (
        <div className="container-fluid">
            <Button
                variant="outline-secondary"
                onClick={() => history.push('/project')}
                className="mb-4"
            >
                ← Quay lại
            </Button>

            <div className="mb-4 border-bottom pb-3">
                <div className="d-flex justify-content-between align-items-end mb-3">
                    <div>
                        <h4 className="mb-0 text-dark" style={{ fontWeight: 600 }}>{item?.name}</h4>
                        <small className="text-muted">Quản lý tiến độ công việc</small>
                    </div>
                    <div style={{ width: '500px' }}>
                        <div className="d-flex justify-content-between mb-2">
                            <span style={{ fontWeight: 700, fontSize: '15px' }}>Tiến độ tổng thể</span>
                            <span style={{ fontWeight: 700, fontSize: '15px' }}>{totalProgress}%</span>
                        </div>
                        <div className="progress" style={{ height: 12, borderRadius: 10 }}>
                            <div
                                className="progress-bar bg-success"
                                role="progressbar"
                                style={{ width: `${totalProgress}%`, borderRadius: 10 }}
                                aria-valuenow={totalProgress}
                                aria-valuemin="0"
                                aria-valuemax="100"
                            />
                        </div>
                    </div>
                </div>

                {item?.members && item.members.length > 0 && (
                    <div className="mt-3">
                        <div className="table-responsive border rounded bg-white">
                            <table className="table table-sm table-hover mb-0" style={{ fontSize: 13 }}>
                                <thead className="bg-light">
                                    <tr>
                                        <th className="ps-3 py-2" style={{ width: '40%' }}>Thành viên tham gia</th>
                                        <th className="text-center py-2" style={{ width: '30%' }}>Trạng thái</th>
                                        <th className="text-center py-2" style={{ width: '30%' }}>Hoàn thành lúc</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {item.members.map((m, i) => {
                                        const mStatusValue = m.status ?? m.Status ?? 0;
                                        const mStatus = TASK_STATUS_CONST.find(s => Number(s.value) === Number(mStatusValue));
                                        return (
                                            <tr key={i}>
                                                <td className="ps-3 py-2 align-middle fw-500">{getAssigneeName(m.userId || m.UserId)}</td>
                                                <td className="text-center py-2 align-middle">
                                                    {(() => {
                                                        const style = getStatusStyle(mStatus?.color || 'secondary');
                                                        return (
                                                            <span 
                                                                style={{ 
                                                                    fontSize: '0.65rem', 
                                                                    padding: '2px 8px',
                                                                    borderRadius: '10px',
                                                                    fontWeight: '600',
                                                                    backgroundColor: style.bg,
                                                                    color: style.text,
                                                                    border: `1px solid ${style.border}`,
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px',
                                                                    textTransform: 'uppercase',
                                                                    whiteSpace: 'nowrap'
                                                                }}
                                                            >
                                                                <span style={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: style.text, flexShrink: 0 }}></span>
                                                                {mStatus?.label || 'Cần làm'}
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="text-center py-2 align-middle text-muted">
                                                    {m.actualEndDate ? toDisplayDate(m.actualEndDate) : '-'}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            <Row className="kanban-board">
                {STATUS_COLUMNS.map(col => {
                    const list = tasks.filter(t => t.status === col.id);
                    const styleCfg = COLUMN_STYLE_MAP[col.id] || COLUMN_STYLE_MAP[0];
                    
                    return (
                        <Col key={col.id} md={3}>
                            <div
                                className="card mb-3 shadow-sm"
                                style={{
                                    borderColor: styleCfg.border,
                                    borderWidth: 1,
                                    borderStyle: 'solid',
                                    backgroundColor: '#ffffff',
                                }}
                            >
                                <div
                                    className="card-header d-flex align-items-center"
                                    style={{
                                        backgroundColor: styleCfg.headerBg,
                                        borderBottomColor: styleCfg.border,
                                        borderBottomWidth: 1,
                                        borderBottomStyle: 'solid',
                                        color: styleCfg.headerText,
                                        fontWeight: 600,
                                        fontSize: 14,
                                        height: 52,
                                    }}
                                >
                                    <span>{col.title}</span>
                                    <div style={{ flex: 1, textAlign: 'center', fontWeight: 500 }}>{list.length}</div>
                                    {col.id === 0 && item?.status !== 1 ? (
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary d-flex align-items-center justify-content-center p-0"
                                            onClick={handleOpenCreate}
                                            style={{ width: 28, height: 28, borderRadius: 6, fontSize: 18 }}
                                        >
                                            +
                                        </button>
                                    ) : (
                                        <div style={{ width: 28 }}></div>
                                    )}
                                </div>
                                <div
                                    className="card-body p-2"
                                    style={{
                                        minHeight: 400,
                                        backgroundColor: styleCfg.bodyBg,
                                    }}
                                >
                                    {col.id === 0 && formMode === 'create' && renderTaskForm()}

                                    {list.map(task => (
                                        formMode === 'edit' && formTask?.id === task.id ? (
                                            <div key={task.id}>{renderTaskForm()}</div>
                                        ) : (
                                            <Card key={task.id} className="mb-2 shadow-sm">
                                                <Card.Body className="p-2">
                                                    <h6 className="mb-2" style={{ fontWeight: '500' }}>{getAssigneeName(task.userId || task.UserId)}</h6>
                                                    
                                                    {task.description && (
                                                        <p className="mb-2 text-muted" style={{ fontSize: 12 }}>
                                                            {task.description}
                                                        </p>
                                                    )}

                                                    <div className="d-flex justify-content-between align-items-start mb-1" style={{ fontSize: 12 }}>
                                                        <div className="flex-grow-1">
                                                            <div className="mb-1 text-muted">Mức độ ưu tiên: <strong className="text-dark">{TASK_PRIORITY_CONST.find(p => p.value === task.priority)?.label || 'Bình thường'}</strong></div>
                                                            <div className="text-muted">Tiến độ: <strong className="text-dark">{task.status === 3 ? 100 : (task.progress || 0)}%</strong></div>
                                                        </div>
                                                        <div className="text-right text-muted" style={{ minWidth: 100 }}>
                                                            {task.startDate && <div>Bắt đầu: {toDisplayDate(task.startDate)}</div>}
                                                            {task.deadline && <div>Hạn chót: {toDisplayDate(task.deadline)}</div>}
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="mb-1 text-muted" style={{ fontSize: 12 }}>
                                                        Người phụ trách: <strong className="text-dark">{getAssigneeName(task.userId || task.UserId)}</strong>
                                                    </div>

                                                    <div className="d-flex justify-content-end gap-3 mt-2">
                                                        {item?.status !== 1 && <span className="text-primary" style={{ cursor: 'pointer', fontSize: '12px', fontWeight: 500 }} onClick={() => handleOpenEdit(task)}>Sửa</span>}
                                                        {item?.status !== 1 && <span className="text-danger" style={{ cursor: 'pointer', fontSize: '12px', fontWeight: 500 }} onClick={() => handleDeleteTask(task.id)}>Xoá</span>}
                                                    </div>
                                                    
                                                    <div className="mt-2 d-flex gap-1 flex-wrap border-top pt-2">
                                                        {item?.status !== 1 && String(task.userId || task.UserId) === String(currentUserId) && STATUS_COLUMNS.filter(c => c.id !== (task.status ?? task.Status)).map(c => (
                                                            <Button key={c.id} variant={`outline-${c.color}`} size="xs" style={{ fontSize: '10px', padding: '2px 4px' }} onClick={() => handleMoveTask(c.id)}>
                                                                → {c.title}
                                                            </Button>
                                                        ))}
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        )
                                    ))}
                                </div>
                            </div>
                        </Col>
                    );
                })}
            </Row>
        </div>
    );
};

export default ProjectKanban;
